import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import "./FileUpload.scss";
import { svgs } from "../../common/assats";
import { useTranslation } from "react-i18next";

interface FileUploadProps {
  onFilesUploaded: (files: File[]) => void;
  uploadFiles?: (
    files: File[],
    onProgress: (progress: number) => void
  ) => Promise<void>;
  showFirmwareFields?: boolean;
  templateUrl?: string;
  templateDescription?: string;
  fileType?: string;
  oneFile?: boolean;
}
export interface FileUploadRef {
  getFirmwareName: () => string;
  getFirmwareNumber: () => string;
  getFileName: () => string;
  getFileType: () => string;
  getFile: () => File | null;
}

const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(
  (
    {
      onFilesUploaded,
      uploadFiles,
      showFirmwareFields = true,
      templateUrl,
      templateDescription,
      fileType: fileExtension,
    },
    ref
  ) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [firmwareName, setFirmwareName] = useState<string>("");
    const [firmwareNumber, setFirmwareNumber] = useState<string>("");
    const [fileType, setFileType] = useState<string>("");

    const [isUploaded, setIsUploaded] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [timeRemaining, setTimeRemaining] = useState<number>(30);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const uploadIntervalRef = useRef<number | null>(null);
    const allowedExtension = (fileExtension || ".xlsx").toLowerCase();
    const { t } = useTranslation();
    const getText = (text: string) => t(`fota.${text}`);

    useImperativeHandle(ref, () => ({
      getFirmwareName: () => firmwareName,
      getFirmwareNumber: () => firmwareNumber,
      getFileName: () => (selectedFiles.length ? selectedFiles[0].name : ""),
      getFileType: () => fileType,
      getFile: () => (selectedFiles.length ? selectedFiles[0] : null),
    }));

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const files = Array.from(e.target.files);
        const filteredFiles = files.filter((file) =>
          file.name.toLowerCase().endsWith(allowedExtension)
        );

        if (filteredFiles.length === 0) {
          alert(`Only ${allowedExtension.toUpperCase()} files are allowed.`);
          return;
        }

        setSelectedFiles(filteredFiles);
        onFilesUploaded(filteredFiles);
        startMockUpload();
        setFileType(filteredFiles[0].type);
      }
    };

    const handleFirmwareNameChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      setFirmwareName(e.target.value);
    };

    const handleFirmwareNumberChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      setFirmwareNumber(e.target.value);
    };
    const handleFileType = (e: React.ChangeEvent<HTMLInputElement>) => {
      console.log(e.target.value);
      setFirmwareNumber(e.target.value);
    };

    const startMockUpload = () => {
      setIsUploading(true);
      setIsUploaded(false);
      setUploadProgress(0);
      setTimeRemaining(5);
      setIsPaused(false);

      if (uploadIntervalRef.current) {
        window.clearInterval(uploadIntervalRef.current);
      }
      uploadIntervalRef.current = window.setInterval(() => {
        setUploadProgress((prev) => {
          if (isPaused || prev >= 100) return prev;
          return prev + 1;
        });
        setTimeRemaining((prev) => {
          if (isPaused || uploadProgress >= 10) return prev;
          return prev > 0 ? prev - 1 : 0;
        });
      }, 300);
    };

    const handlePause = () => {
      setIsPaused(!isPaused);
    };

    useEffect(() => {
      if (!isPaused && isUploading && uploadIntervalRef.current == null) {
        startMockUpload();
      }
    }, [isPaused]);

    useEffect(() => {
      if (uploadProgress >= 100 && isUploading) {
        setIsUploading(false);
        setIsUploaded(true);
        if (uploadIntervalRef.current) {
          window.clearInterval(uploadIntervalRef.current);
          uploadIntervalRef.current = null;
        }
      }
    }, [uploadProgress, isUploading]);

    const handleCancel = () => {
      setIsUploading(false);
      setUploadProgress(0);
      setTimeRemaining(4);
      setIsPaused(false);
      if (uploadIntervalRef.current) {
        window.clearInterval(uploadIntervalRef.current);
        uploadIntervalRef.current = null;
      }
    };

    // ====== Real Upload Flow (Optional) ======
    const handleUpload = async () => {
      if (!selectedFiles.length) return;
      // If you want to skip mock logic, remove startMockUpload() below
      // startMockUpload();

      // If you have an actual "uploadFiles" prop:
      /*
      try {
        setIsUploading(true);
        await uploadFiles(selectedFiles, (progress) => {
          setUploadProgress(progress);
        });
      } catch (error) {
        console.error("Upload failed:", error);
      } finally {
        setIsUploading(false);
      }
      */
    };

    const formatFileSize = (size: number) => {
      if (size < 1024) return size + " B";
      if (size < 1024 * 1024) return (size / 1024).toFixed(2) + " KB";
      if (size < 1024 * 1024 * 1024)
        return (size / (1024 * 1024)).toFixed(1) + " MB";
      return (size / (1024 * 1024 * 1024)).toFixed(2) + " GB";
    };

    const removeFile = (file: File) => {
      const filtered = selectedFiles.filter((f) => f !== file);
      setSelectedFiles(filtered);
      if (!filtered.length) {
        setIsUploaded(false);
        setUploadProgress(0);
      }
    };

    // ====== Drag & Drop ======
    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const files = Array.from(e.dataTransfer.files);
      const filteredFiles = files.filter((file) =>
        file.name.toLowerCase().endsWith(allowedExtension)
      );

      if (filteredFiles.length === 0) {
        alert(`Only ${allowedExtension.toUpperCase()} files are allowed.`);
        return;
      }

      setSelectedFiles(filteredFiles);
      onFilesUploaded(filteredFiles);
      startMockUpload();
      setFileType(filteredFiles[0].type);
    };

    return (
      <div className="file-upload-step">
        {/* Conditionally render firmware fields */}
        {showFirmwareFields && (
          <div className="firmware-details">
            <label htmlFor="firmware-name">
              {getText("sort.firmwareVersionName")}
              <input
                id="firmware-name"
                type="text"
                value={firmwareName}
                onChange={handleFirmwareNameChange}
                required
              />
            </label>

            <label htmlFor="firmware-number">
              {getText("sort.firmwareVersionNumber")}
              <input
                id="firmware-number"
                type="text"
                value={firmwareNumber}
                onChange={handleFirmwareNumberChange}
                required
              />
            </label>
          </div>
        )}

        {/* Drag & Drop area */}
        <div
          className="upload-box"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="upload-icon">
            <svgs.fota.backup />
          </div>
          <div className="upload-instructions">
            <span className="drag-text">{getText("File_Drage")}</span>
            <div className="separator">
              <span>{getText("or")}</span>
            </div>
            <label className="browse-button">
              {getText("Browse_Files")}
              <input
                type="file"
                accept={allowedExtension}
                multiple={false}
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
            </label>
          </div>
        </div>
        <p className="file-support-text">
          {templateUrl && templateDescription ? (
            <a href={templateUrl} download className="template-link">
              {templateDescription}
            </a>
          ) : allowedExtension ? (
            <>Only support {allowedExtension} files</>
          ) : (
            getText("File_Support")
          )}
        </p>

        {/* Upload button (only shown if files selected & not uploading) */}
        {/* {!!selectedFiles.length && !isUploading && uploadProgress < 100 && (
            <button onClick={handleUpload}>{getText("Next_To_Upload")}</button>
          )} */}

        {/* Mock Upload Progress Bar */}
        {/* {isUploading && (
            <div className="uploading-container">
              <div className="progress-bar">
                <div className="progress-bar-inner" style={{ width: `${uploadProgress}%` }} />
              </div>
              <div className="progress-info">
                <strong>{getText("uploading...")}</strong>
                <div className="progress-details">
                  {uploadProgress}% • {timeRemaining} {getText("seconds_Remaining")}
                </div>
              </div>
              <div className="progress-actions">
                <button onClick={handlePause}>
                  {isPaused ? <svgs.fota.pause /> : <svgs.fota.pause />}
                </button>
                <button onClick={handleCancel}>
                  <svgs.fota.cancel />
                </button>
              </div>
            </div>
          )} */}

        {!!selectedFiles.length && (
          <div className="file-list-container">
            {selectedFiles.map((file, i) => (
              <div key={i} className="uploaded-file-card">
                <div className="file-icon">
                  <svgs.fota.folder />
                </div>
                <div className="file-info">
                  <div className="file-name">{file.name}</div>
                  <div className="file-size">{formatFileSize(file.size)}</div>
                </div>
                <button
                  className="file-remove-button"
                  onClick={() => removeFile(file)}
                >
                  <svgs.fota.cancel />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

export default FileUpload;
